/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export interface NumberInputProps extends FieldRenderProps<number, HTMLInputElement> {
  placeholder?: string;
  type: string;
  readOnly?: boolean;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  input,
  meta,
  ...rest
}: NumberInputProps): React.ReactElement => {
  const { id, type, placeholder, readOnly, max, min, disabled, stepValue, showTooltip } = rest;

  return (
    <OverlayTrigger
      key={id}
      placement="top"
      overlay={
        showTooltip && !disabled ? (
          <Tooltip id={`tooltip-${id}`}>{`max: ${max} | min: ${min} | step: ${stepValue}`}</Tooltip>
        ) : (
          <span />
        )
      }
    >
      <input
        {...input}
        id={input.name as string}
        type={type}
        readOnly={readOnly}
        placeholder={placeholder}
        step={stepValue}
        className={`form-control p-0 ${disabled ? 'text-gray' : ''}`}
        max={max}
        min={min}
        disabled={disabled}
      />
    </OverlayTrigger>
  );
};

export interface NumberInputFieldProps {
  id?: number;
  name: string;
  type: string;
  label?: string;
  labelClass?: string;
  placeholder?: string;
  readOnly?: boolean;
  selValue?: number;
  stepValue?: number;
  max?: number | null;
  min?: number | null;
  unit?: string;
  dataType?: string;
  disabled?: boolean;
  description?: string;
  showTooltip: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  format?: (value: number | null, name: string) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parse?: (value: any | null, name: string) => number;
  validationError?: string;
}

export const NumberInputField: React.FC<NumberInputFieldProps> = ({
  onChange: handleChange,
  ...props
}: NumberInputFieldProps): React.ReactElement => {
  const { name, label, labelClass, unit, selValue, max, min, format, parse, disabled, validationError } = props;

  return (
    // eslint-disable-next-line no-restricted-globals
    <Field name={name} defaultValue={selValue} format={format} parse={parse}>
      {({ input: { onChange, ...restInputProps }, ...fieldProps }) => {
        const isValueInvalid =
          validationError ||
          (props && typeof max === 'number' && restInputProps.value !== undefined && restInputProps.value > max) ||
          (props && typeof min === 'number' && restInputProps.value !== undefined && restInputProps.value < min) ||
          Number.isNaN(Number(restInputProps.value));

        const isFieldInvalid = fieldProps.meta.touched && fieldProps.meta.invalid;
        const isHigherInvalid = props && typeof max === 'number' && restInputProps.value && restInputProps.value > max;
        const isLowerInvalid = props && typeof min === 'number' && restInputProps.value && restInputProps.value < min;

        return (
          <div className="form-group">
            {label && (
              <div className="row">
                <div className="col">
                  <label
                    className={`parameter-label ${labelClass} ${
                      (validationError || isFieldInvalid || isValueInvalid) && !disabled ? 'invalid-feedback-label' : ''
                    } ${disabled ? 'text-gray' : ''}`}
                    htmlFor={restInputProps.name}
                  >
                    {label}
                    {unit && unit !== '' && <span>[{unit}]</span>}
                  </label>
                </div>
              </div>
            )}
            <div className="form-label-group">
              <NumberInput
                {...fieldProps}
                {...props}
                input={{
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(event);
                    if (handleChange) {
                      handleChange(event);
                    }
                  },
                  ...restInputProps,
                }}
              />
              {fieldProps && typeof props !== undefined && props && (
                <div className="invalid-feedback">
                  {fieldProps.meta.touched && fieldProps.meta.error && <span>{fieldProps.meta.error}</span>}
                </div>
              )}

              {validationError && !disabled ? (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  <span>{validationError}</span>
                </div>
              ) : (
                ''
              )}
              {isHigherInvalid && !disabled ? (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  <span>Higher than allowed maximum</span>
                </div>
              ) : (
                ''
              )}
              {isLowerInvalid && !disabled ? (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  <span>Lower than allowed minimum</span>
                </div>
              ) : (
                ''
              )}
              {fieldProps && props && restInputProps && Number.isNaN(Number(restInputProps.value)) && !disabled ? (
                <div className="invalid-feedback">
                  <span>This field is required</span>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        );
      }}
    </Field>
  );
};

NumberInputField.defaultProps = { labelClass: '' };
